import { useTranslation } from 'react-i18next'
import { Columns } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export type TableColumnMenuItem = { id: string; label: string }

type TableColumnVisibilityMenuProps = {
  columns: TableColumnMenuItem[]
  visibility: Record<string, boolean>
  onToggle: (columnId: string) => void
  align?: 'start' | 'end'
  /** Prevent hiding every column (default: at least one stays on). */
  minVisible?: number
  triggerLabel?: string
  menuTitle?: string
  disabled?: boolean
}

export function TableColumnVisibilityMenu({
  columns,
  visibility,
  onToggle,
  align = 'end',
  minVisible = 1,
  triggerLabel,
  menuTitle,
  disabled = false,
}: TableColumnVisibilityMenuProps) {
  const { t } = useTranslation('common')
  const visibleCount = columns.filter((c) => visibility[c.id]).length

  const handleToggle = (id: string) => {
    if (visibility[id] && visibleCount <= minVisible) return
    onToggle(id)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button variant="outline" size="sm" type="button" disabled={disabled}>
          <Columns className="mr-2 h-4 w-4" />
          {triggerLabel ?? t('tableColumnVisibility.columns')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-56 max-h-[min(70vh,24rem)] overflow-y-auto">
        <DropdownMenuLabel>{menuTitle ?? t('tableColumnVisibility.showColumns')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {columns.map((col) => (
          <DropdownMenuCheckboxItem
            key={col.id}
            checked={Boolean(visibility[col.id])}
            onCheckedChange={() => handleToggle(col.id)}
          >
            {col.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** All listed column ids start visible. */
export function initialColumnVisibility(ids: string[]): Record<string, boolean> {
  return Object.fromEntries(ids.map((id) => [id, true]))
}

export function countVisibleColumns(visibility: Record<string, boolean>): number {
  return Object.values(visibility).filter(Boolean).length
}

/** How many of `orderedIds` (in order) are visible, considering only indices `[start, end)` */
export function countVisibleInRange(
  visibility: Record<string, boolean>,
  orderedIds: readonly string[],
  start: number,
  end: number,
): number {
  let n = 0
  for (let i = start; i < end && i < orderedIds.length; i++) {
    if (visibility[orderedIds[i]]) n++
  }
  return n
}
